
const GiadaSection = () => {
  const handleCTAClick = (context: string, cta: string) => {
    // Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'cta_click', {
        context: context,
        cta: cta
      });
    }
  };

  const handleGiadaDatesClick = () => {
    handleCTAClick('giada', 'giada_dates');
    window.open('https://giada.org/pre-license-class/', '_blank', 'noopener,noreferrer');
  };

  const handleStartupChecklistClick = () => {
    handleCTAClick('giada', 'startup_checklist');
    // Navigate to internal checklist page or download
    window.location.href = '/resources#startup-checklist';
  };

  return (
    <section id="giada" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              GIADA Pre-License Seminar (State-Required)
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete the required pre-license education seminar through GIADA. 
              This certificate is mandatory for your Georgia dealer license application.
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What You'll Learn</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-book-line text-blue-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Georgia dealer laws and compliance basics</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-file-text-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Required forms and documentation procedures</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checklist-line text-purple-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Comprehensive startup checklist</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-car-line text-yellow-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Introduction to title work and transfers</span>
                </li>
              </ul>
            </div>
            <div>
              <img 
                alt="GIADA pre-license seminar classroom education Georgia dealer license requirements professional training"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
                src="https://public.readdy.ai/ai/img_res/909526f3620f5ea90f5a94307d0e4627.jpg"
                width="600"
                height="400"
                loading="lazy"
              />
            </div>
          </div>

          {/* Steps Process */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">Simple 3-Step Process</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Pick a Date</h4>
                <p className="text-gray-600">
                  Choose from available GIADA seminar dates that fit your schedule
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Attend Class</h4>
                <p className="text-gray-600">
                  Complete the full-day seminar and pass the required examination
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">3</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Submit Certificate</h4>
                <p className="text-gray-600">
                  Include your completion certificate with your dealer license application
                </p>
              </div>
            </div>
          </div>

          {/* Important Info Box */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 mb-12">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-yellow-600"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Important Requirements</h4>
                <ul className="text-gray-700 space-y-1 text-sm">
                  <li>• Certificate must be obtained within 12 months of application</li>
                  <li>• Seminar fee is typically $150-200 (paid directly to GIADA)</li>
                  <li>• Bring valid photo ID and be prepared for full-day attendance</li>
                  <li>• Certificate is required for both new and renewal applications</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button
                onClick={handleGiadaDatesClick}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-blue-600 hover:to-blue-700 transition-all whitespace-nowrap cursor-pointer"
              >
                View GIADA Class Dates
                <i className="ri-external-link-line ml-2"></i>
              </button>
              
              <button
                onClick={handleStartupChecklistClick}
                className="border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                Download Startup Checklist
                <i className="ri-download-line ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GiadaSection;
